import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarClock, MessageCircle } from "lucide-react";
import { api, eur } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { useT, localized } from "@/i18n";

export function ServiceCard({ s, requestLink, locale, t }) {
  return (
    <article data-testid={`service-card-${s.slug}`} className="card-craft overflow-hidden flex flex-col group">
      <div className="overflow-hidden h-44"><img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
      <div className="p-5 flex-1 flex flex-col">
        <span className="font-mono text-xs text-ink/55" data-testid={`service-price-${s.slug}`}>{s.price_note ? s.price_note : <>{t("common.from")} {eur(s.price_from)}/{s.unit}</>}</span>
        <h3 className="mt-1 font-serif text-xl font-semibold leading-tight">{localized(s, "name", locale)}</h3>
        <p className="mt-2 text-sm text-ink/60 flex-1 leading-relaxed">{localized(s, "description", locale)}</p>
        <Link to={requestLink(s.slug)} data-testid={`btn-richiedi-${s.slug}`} className="btn-primary mt-4 !py-2 !px-4 text-sm self-start">{t("cta.requestQuote")} <ArrowRight size={14} /></Link>
      </div>
    </article>
  );
}

export default function AreaPage() {
  const areaSlug = useLocation().pathname.replace(/^\//, "").split("/")[0];
  const [area, setArea] = useState(null);
  const { user, config } = useAuth();
  const { t, locale } = useT();
  useEffect(() => { api.get("/areas").then((r) => setArea(r.data.find((a) => a.slug === areaSlug) || null)); }, [areaSlug]);
  const requestLink = (slug, qs = "") => (user ? `/app/richiesta/${slug}${qs}` : `/login?next=${encodeURIComponent(`/app/richiesta/${slug}${qs}`)}`);
  if (!area) return <Shell><p className="text-sm text-ink/50">{t("common.loading")}</p></Shell>;
  const wa = config?.contact_phone ? `https://wa.me/${config.contact_phone.replace(/\D/g, "")}` : "/#contatti";

  return (
    <Shell>
      <section className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 rise">
          <p className="eyebrow">Ci penso io</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight" data-testid="area-title">{localized(area, "short", locale)}</h1>
          <p className="mt-5 text-lg text-ink/80 font-medium leading-snug">“{localized(area, "claim", locale)}”</p>
          <p className="mt-4 max-w-xl text-ink/65 leading-relaxed">{localized(area, "description", locale)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={requestLink("segnala-problema")} data-testid="area-cta-intervento" className="btn-primary">{t("cta.requestIntervention")}</Link>
            <Link to={requestLink("check-up-casa", "?path=inspection")} data-testid="area-cta-sopralluogo" className="btn-secondary">{t("cta.bookInspection")}</Link>
            <a href={wa} target="_blank" rel="noreferrer" data-testid="area-cta-consulenza" className="btn-ghost"><MessageCircle size={16} /> {t("cta.askConsultation")}</a>
          </div>
        </div>
        <div className="lg:col-span-6 rise rise-2"><img src={area.image} alt={area.short} className="h-[380px] w-full rounded-[2rem] object-cover border border-craft" /></div>
      </section>

      {area.subcategories.map((sc) => (
        <section key={sc.key} className="mt-20" data-testid={`subcategory-${sc.key}`}>
          <div className="flex items-end justify-between gap-4 flex-wrap"><h2 className="font-serif text-3xl font-semibold">{localized(sc, "name", locale)}</h2><span className="text-xs text-ink/50 font-mono">{t("home.servicesCount", { n: sc.services.length })}</span></div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sc.services.map((s) => <ServiceCard key={s.slug} s={s} requestLink={requestLink} locale={locale} t={t} />)}
          </div>
        </section>
      ))}

      <section className="mt-24 rounded-[2rem] bg-forest text-white p-8 sm:p-10 grid md:grid-cols-12 gap-6 items-center grain">
        <div className="md:col-span-8"><p className="eyebrow !text-terracotta-light">{t("p365.eyebrow")}</p><h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold">{t("area.pref")}</h2><p className="mt-3 text-white/80 leading-relaxed">{t("area.prefText")}</p></div>
        <div className="md:col-span-4 flex md:justify-end"><Link to="/365" data-testid="area-cta-365" className="btn-primary"><CalendarClock size={16} /> {t("cta.discover")} 365</Link></div>
      </section>
    </Shell>
  );
}
